/**
 * Tema dinâmico buscando do backend
 *
 * Referências:
 * 1. https://stackblitz.com/edit/angular-material-theming-playground?file=src%2Fapp%2Fapp.component.ts%3AL77
 * 2. https://webfactory.mk/an-approach-to-dynamic-theme-color-generation-in-angular/
 */

import * as _ from 'lodash';
import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { tinycolor, TinyColor } from '@ctrl/tinycolor';
import { ThemeRepository } from '@data/theme/theme.repository';
import { ThemeDto } from '@infra/theme/dto/theme.dto';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Injectable({
  providedIn: 'root',
})
class ThemeService implements OnDestroy {
  private static readonly THEME_VARIABLE_PREFIX = '--theme';
  private readonly themeKey = 'theme';
  private readonly renderer: Renderer2;

  get theme(): ThemeDto {
    const theme = localStorage.getItem(this.themeKey);
    return theme ? JSON.parse(theme) : null;
  }

  set theme(theme: ThemeDto) {
    if (theme) {
      localStorage.setItem(this.themeKey, JSON.stringify(theme));
    }
  }

  get primaryColor(): string {
    return this.theme?.corPrimaria || '';
  }

  get accentColor(): string {
    return this.theme?.corSecundaria || '';
  }

  get urlImagemLogin(): string {
    return this.theme?.urlImagemLogin || '';
  }

  get urlImagemToolbar(): string {
    return this.theme?.urlImagemToolbar || '';
  }

  get urlImagemBackground(): string {
    return this.theme?.urlImagemBackground || '';
  }

  get linkPortal(): string {
    return this.theme?.linkPortal || '';
  }

  get identificador(): string {
    return this.linkPortal.split('/').pop() || '';
  }

  constructor(
    private router: Router,
    private rendererFactory: RendererFactory2,
    private themeRepository: ThemeRepository,
    private authService: AuthService,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.renderer?.destroy();
  }

  async initialize(): Promise<any> {
    if(this.authService.isAuthenticated && this.theme) {
      return Promise.resolve(this.buildTheme()); 
    }

    const promise = new Promise((resolve, reject) => {
      this.themeRepository.getTheme().subscribe({
        next: (theme) => {
          if (!_.isEqual(theme, this.theme)) {
            this.theme = theme;
          }
          
          return resolve(this.buildTheme());
        },
        error: (error) => {
          if(this.theme) {
            this.buildTheme()
            this.router.navigate([this.identificador]);
            
            return resolve(true);
          }

          this.router.navigate(['/']);

          return reject(error);
        }
      });
    });

    return promise;
  }

  private buildTheme() {
    const primaryColorPalette = this.computeColors(this.theme.corPrimaria);
    const accentColorPalette = this.computeColors(this.theme.corSecundaria);
    const successColorPalette = this.computeColors('#28a745');
    const dangerColorPalette = this.computeColors('#bd2130');
    const warnColorPalette = this.computeColors('#ffc107');

    this.updateTheme(primaryColorPalette, 'primary');
    this.updateTheme(accentColorPalette, 'accent');
    this.updateTheme(successColorPalette, 'success');
    this.updateTheme(dangerColorPalette, 'danger');
    this.updateTheme(warnColorPalette, 'warn');
    
    this.saveImageUrl(this.theme.urlImagemLogin, '--theme-login-image');
    this.saveImageUrl(this.theme.urlImagemToolbar, '--theme-toolbar-image');
    this.saveImageUrl(this.theme.urlImagemBackground, '--theme-background-image');

    return {
      primaryColor: this.theme.corPrimaria,
      accentColor: this.theme.corSecundaria,
      urlImagemLogin: this.theme.urlImagemLogin,
      urlImagemToolbar: this.theme.urlImagemToolbar,
      urlImagemBackground: this.theme.urlImagemBackground,
    };
  }

  private getColor(value: TinyColor, name: string): Color {
    const color = tinycolor(value);

    return {
      name,
      hex: color.toHexString(),
      darkContrast: color.isLight(),
    };
  }

  private computeColors(hex: string): Color[] {
    return [
      this.getColor(tinycolor(hex).lighten(52), '50'),
      this.getColor(tinycolor(hex).lighten(37), '100'),
      this.getColor(tinycolor(hex).lighten(26), '200'),
      this.getColor(tinycolor(hex).lighten(12), '300'),
      this.getColor(tinycolor(hex).lighten(6), '400'),
      this.getColor(tinycolor(hex), '500'),
      this.getColor(tinycolor(hex).darken(6), '600'),
      this.getColor(tinycolor(hex).darken(12), '700'),
      this.getColor(tinycolor(hex).darken(18), '800'),
      this.getColor(tinycolor(hex).darken(24), '900'),
      this.getColor(tinycolor(hex).lighten(50).saturate(30), 'A100'),
      this.getColor(tinycolor(hex).lighten(30).saturate(30), 'A200'),
      this.getColor(tinycolor(hex).lighten(10).saturate(15), 'A400'),
      this.getColor(tinycolor(hex).lighten(5).saturate(5), 'A700'),
    ];
  }

  private updateTheme(colors: Color[], theme: string) {
    colors.forEach((color) => {
      document.documentElement.style.setProperty(
        `${ThemeService.THEME_VARIABLE_PREFIX}-${theme}-${color.name}`,
        color.hex
      );

      document.documentElement.style.setProperty(
        `${ThemeService.THEME_VARIABLE_PREFIX}-${theme}-contrast-${color.name}`,
        color.darkContrast ? 'rgba(black, 0.87)' : 'white'
      );
    });
  }

  private saveImageUrl(url: string, variable: string) {
    document.documentElement.style.setProperty(variable, `url(${url})`);
  }
}

export { ThemeService };
